import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, ArrowRight, Eye, EyeOff, Mail, Compass } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Justice } from "@/components/Justice";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

function RecoveryDialog({ open, onOpenChange, mode }) {
  // mode: "password" | "email"
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const isPw = mode === "password";

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(isPw ? "/auth/forgot-password" : "/auth/recover-account", { email });
      setSent(true);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setSent(false); setEmail(""); } }}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <DialogHeader>
          <DialogTitle className="font-head font-semibold tracking-tight">
            {isPw ? "Reset your password" : "Recover your account"}
          </DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            {isPw
              ? "Enter your account email and we'll send a secure reset link."
              : "Enter your email and we'll send your account sign-in details to that address."}
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="py-4 flex items-start gap-3" data-testid="recovery-sent">
            <Mail className="h-5 w-5 mt-0.5" style={{ color: "var(--ek-secure)" }} />
            <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>
              If an account exists for <b style={{ color: "var(--ek-text)" }}>{email}</b>, an email is on its way. Check your inbox.
            </p>
          </div>
        ) : (
          <div className="py-2 space-y-3">
            <Label htmlFor="rec-email">Email</Label>
            <Input id="rec-email" data-testid="recovery-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-transparent" placeholder="you@example.com" />
          </div>
        )}
        {!sent && (
          <DialogFooter>
            <Button onClick={submit} disabled={busy || !email} data-testid="recovery-submit-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              {busy ? "SENDING..." : "Send"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 384 512" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
    </svg>
  );
}

export default function Auth() {
  const { login, register, enterGuest } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState({ open: false, mode: "password" });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const appleSignIn = async () => {
    if (window.Capacitor?.isNativePlatform?.()) {
      try {
        const { data } = await api.post("/auth/apple/native", {});
        if (data?.token) window.location.reload();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Sign in with Apple is not available right now.");
      }
      return;
    }
    toast.message("Sign in with Apple is available in the EvidenceKeeper iOS app.");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: brand + Justice */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r relative overflow-hidden ek-scanline" style={{ background: "var(--ek-surface)" }}>
        <div className="flex items-center gap-3" data-testid="brand-logo">
          <img src="/ek-logo.png" alt="EvidenceKeeper" className="h-11 w-11 object-contain" />
          <span className="font-head font-bold tracking-tight text-lg">EvidenceKeeper</span>
        </div>

        <div className="max-w-md">
          <div className="flex items-end gap-4 mb-6">
            <Justice size={128} />
            <div className="border px-4 py-3 mb-4 relative" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }}>
              <span className="absolute -left-2 bottom-4 h-3 w-3 rotate-45 border-l border-b" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }} />
              <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>Hi, I'm Justice. Let's document what you observed today.</p>
            </div>
          </div>
          <h1 className="font-head font-bold text-4xl leading-tight tracking-tight mb-4">
            Document. Preserve. Protect.
          </h1>
          <p className="text-base leading-relaxed" style={{ color: "var(--ek-text-secondary)" }}>
            A high-precision record of observable facts. Encrypted photos, PDFs and
            voice recordings, timestamped and preserved.
          </p>
          <div className="mt-8 flex items-center gap-2 ek-stamp">
            <Lock className="h-3.5 w-3.5" /> AES-256 ENCRYPTED VAULT
          </div>
        </div>

        <div className="ek-mono-label">HERRELL HOMESTEAD LLC · SECURE DOCUMENTATION</div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src="/ek-logo.png" alt="EvidenceKeeper" className="h-10 w-10 object-contain" />
            <span className="font-head font-bold tracking-tight text-lg">EvidenceKeeper</span>
          </div>

          <div className="ek-stamp inline-block mb-3">{mode === "login" ? "SECURE SIGN IN" : "CREATE ACCOUNT"}</div>
          <h2 className="font-head text-3xl font-semibold tracking-tight mb-8">
            {mode === "login" ? "Access your vault" : "Start documenting"}
          </h2>

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-transparent" placeholder="Full name" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-transparent" placeholder="you@example.com" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "login" && (
                  <button type="button" data-testid="forgot-password-link" onClick={() => setRecovery({ open: true, mode: "password" })} className="text-xs transition-colors hover:text-slate-200" style={{ color: "var(--ek-text-muted)" }}>
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Input id="password" data-testid="auth-password-input" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-transparent pr-10" placeholder="••••••••" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition-colors">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && <p data-testid="auth-error" className="text-sm" style={{ color: "var(--ek-error)" }}>{error}</p>}

            <Button type="submit" disabled={busy} data-testid="auth-submit-btn" className="w-full rounded-md font-medium group" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              {busy ? (mode === "login" ? "SIGNING IN..." : "CREATING...") : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>

            {mode === "register" && (
              <p className="text-xs leading-relaxed" style={{ color: "var(--ek-text-muted)" }} data-testid="signup-legal-note">
                <b style={{ color: "var(--ek-text-secondary)" }}>Not legal advice.</b> EvidenceKeeper helps you preserve and organize records; it is not a law firm. By creating an account you agree to our{" "}
                <a href="/terms" className="underline hover:text-slate-200">Terms of Service</a> and{" "}
                <a href="/privacy" className="underline hover:text-slate-200">Privacy Policy</a>.
              </p>
            )}
          </form>

          {/* Sign in with Apple */}
          <div className="mt-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px flex-1" style={{ background: "var(--ek-border)" }} />
              <span className="ek-mono-label">OR</span>
              <div className="h-px flex-1" style={{ background: "var(--ek-border)" }} />
            </div>
            <Button type="button" onClick={appleSignIn} data-testid="apple-signin-btn"
              className="w-full rounded-md font-medium" style={{ background: "#000", color: "#fff" }}>
              <AppleGlyph /> <span className="ml-2">Sign in with Apple</span>
            </Button>
          </div>

          <div className="mt-6 flex flex-col gap-2 text-sm">
            <button type="button" data-testid="auth-toggle-btn" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-left transition-colors hover:text-slate-200" style={{ color: "var(--ek-text-muted)" }}>
              {mode === "login" ? "No account yet? Create one" : "Already have an account? Sign in"}
            </button>
            {mode === "login" && (
              <button type="button" data-testid="forgot-email-link" onClick={() => setRecovery({ open: true, mode: "email" })} className="text-left transition-colors hover:text-slate-200" style={{ color: "var(--ek-text-muted)" }}>
                Forgot your email / username?
              </button>
            )}
          </div>

          <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--ek-border)" }}>
            <Button type="button" variant="outline" data-testid="explore-guest-btn" onClick={enterGuest}
              className="w-full rounded-md font-medium" style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-text-secondary)" }}>
              <Compass className="mr-2 h-4 w-4" /> Explore as guest
            </Button>
            <p className="text-xs text-center mt-2" style={{ color: "var(--ek-text-muted)" }}>Look around first — sign up anytime to save records.</p>
          </div>
        </motion.div>
      </div>

      <DisclaimerFooter />
      <RecoveryDialog open={recovery.open} mode={recovery.mode} onOpenChange={(v) => setRecovery((r) => ({ ...r, open: v }))} />
    </div>
  );
}
